<script lang="ts">
  /* What a current-aware route actually says: when you leave, when you are at
   * each pass, and where you wait.
   *
   * ═══ THE WAIT IS THE FEATURE, SO IT IS A ROW ═══
   *
   * A schedule that folded "hold off Dodd for 35 minutes" into a duration would
   * hide the one thing this router does that the old one could not. It gets its
   * own line, named, with the slack it is waiting for — because "why is this
   * route slower than the straight one" has to have a visible answer.
   *
   * ═══ AND IT SAYS WHAT IT DID NOT KNOW ═══
   *
   * `currentsUsed` false means the model field did not reach this departure or
   * this water, so the passage was timed on boat speed and the narrows alone.
   * That is a perfectly useful plan and a materially weaker claim, and the
   * difference is invisible unless it is written down. Same rule the layers
   * panel follows: absence is a fact, not a failure.
   *
   * Shared by the dash and the hosted chart, so the two cannot drift into
   * describing the same schedule differently.
   */
  import type { Schedule } from '@gennetay/chart-core/lib/passage';
  import { underwayMs, waitingMs } from '@gennetay/chart-core/lib/passage';

  let {
    schedule,
    /** Names for the waypoints, when the host has them. Falls back to indices. */
    labels = []
  }: { schedule: Schedule; labels?: string[] } = $props();

  const hhmm = (t: number) =>
    new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

  /** Days between two instants, so a schedule that runs past midnight says so
   *  rather than appearing to arrive before it left. */
  const dayShift = (from: number, to: number) =>
    Math.round(new Date(to).setHours(0, 0, 0, 0) / 86_400_000) -
    Math.round(new Date(from).setHours(0, 0, 0, 0) / 86_400_000);

  const stamp = (t: number) => {
    const d = dayShift(schedule.departT, t);
    return d > 0 ? `${hhmm(t)} +${d}d` : hhmm(t);
  };

  const dur = (ms: number) => {
    const m = Math.round(ms / 60_000);
    return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
  };

  const waiting = $derived(waitingMs(schedule));
  const underway = $derived(underwayMs(schedule));

  /** Waypoint rows and wait rows, in the order they happen. `etaAt` is one per
   *  waypoint; waits carry the LEG they interrupt, so they slot in after it. */
  const rows = $derived(
    schedule.etaAt.flatMap((t, i) => [
      { kind: 'point' as const, t, label: labels[i] ?? (i === 0 ? 'DEPART' : `WP ${i}`) },
      ...schedule.waits
        .filter((w) => w.atIndex === i)
        .map((w) => ({ kind: 'wait' as const, t: w.from, until: w.untilT, gate: w.gate }))
    ])
  );
</script>

<div class="sched">
  <div class="lbl">SCHEDULE</div>

  <dl class="tot">
    <dt>DEPART</dt>
    <dd>{stamp(schedule.departT)}</dd>
    <dt>ARRIVE</dt>
    <dd>{stamp(schedule.arriveT)}</dd>
    <dt>UNDER WAY</dt>
    <dd>{dur(underway)}</dd>
    {#if waiting > 0}
      <!-- Never folded into "under way". A route that is 40 minutes slower
           because it holds for slack is a different plan from one that is 40
           minutes slower because the tide is foul, and the skipper is the one
           who gets to decide which they wanted. -->
      <dt class="wait">WAITING</dt>
      <dd class="wait">{dur(waiting)}</dd>
    {/if}
  </dl>

  <ol class="legs">
    {#each rows as r, i (i)}
      {#if r.kind === 'point'}
        <li class="pt"><span class="t">{stamp(r.t)}</span><span class="n">{r.label}</span></li>
      {:else}
        <li class="hold">
          <span class="t">{stamp(r.t)}</span>
          <span class="n">
            <strong>{r.gate.name}</strong> — hold {dur(r.until - r.t)} for slack, go at
            {stamp(r.until)}
          </span>
        </li>
      {/if}
    {/each}
  </ol>

  {#if !schedule.currentsUsed}
    <!-- The honest degrade, said out loud. The gates come from CHS station
         predictions that cover the year; the surface-current model reaches a
         day or two. A departure past that still gets its narrows timed, and
         that is a weaker claim than a fully current-optimised route. -->
    <p class="note">
      Timed on the narrows and boat speed only — no surface-current model covers this departure.
    </p>
  {/if}

  {#if schedule.impossibleAt !== null}
    <p class="warn">
      Stops at leg {schedule.impossibleAt + 1}: the stream there is stronger than the boat can make
      good. A later departure, or a faster one, may get through.
    </p>
  {/if}

  {#if schedule.unknownGate}
    <p class="warn">
      No slack prediction left for {schedule.unknownGate.name} — the shipped data ends before this
      passage. Check the tables.
    </p>
  {/if}

  <!-- Not decoration. The schedule states a slack to the minute, which invites
       more trust than a line on a chart does. -->
  <p class="note">
    Advisory. Predicted streams and a speed you entered — verify against the current tables before
    committing to a pass.
  </p>
</div>

<style>
  .sched {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .lbl {
    font: 10px/1 var(--mono);
    letter-spacing: 0.1em;
    color: var(--sub);
  }
  .tot {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 2px 10px;
    margin: 0;
    font: 11px/1.4 var(--mono);
  }
  .tot dt {
    color: var(--sub);
    letter-spacing: 0.06em;
  }
  .tot dd {
    margin: 0;
    color: var(--ink);
  }
  .tot .wait {
    color: var(--am);
  }
  .legs {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
    border-top: 1px solid var(--line);
    padding-top: 6px;
  }
  .legs li {
    display: flex;
    gap: 8px;
    font: 11px/1.35 var(--mono);
  }
  .t {
    color: var(--sub);
    flex: none;
    /* A fixed column so the times line up and the eye can run down them. */
    min-width: 5.5em;
  }
  .n {
    color: var(--ink);
    min-width: 0;
  }
  .hold .n {
    color: var(--am);
    font-family: var(--sans);
    font-size: 11px;
  }
  .note {
    margin: 0;
    font: 9px/1.4 var(--sans);
    color: var(--sub);
  }
  .warn {
    margin: 0;
    font: 10px/1.4 var(--sans);
    color: var(--am);
  }
</style>
